import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { downloadMedia } from "@/components/common/adCard/actions/DownloadAction";
import {
  ArrowDownToLine,
  ChevronsDownUp,
  MoveDiagonal,
  PlayCircle,
  Image,
  X,
  Component,
} from "lucide-react";
import { CreativeReportMediaItem } from "../../types";
import ReactPlayer from "react-player";
import "./../player.module.css";
import { cn } from "@/lib/utils";

export const getMediaIcon = (
  mediaType: string,
  size: number = 12,
  color: string = "#67749B",
  className: string = "",
) => {
  const type = mediaType.toLowerCase();
  const commonProps = {
    size: size,
    style: { color: color },
    className: "flex-shrink-0", // Prevent icon from shrinking
  };

  if (type === "mixed") {
    return <Component {...commonProps} className={cn(className)} />;
  }
  if (type === "video" || type === "videos") {
    return <PlayCircle {...commonProps} className={cn(className)} />;
  }
  return <Image {...commonProps} className={cn(className)} />;
};

interface MediaHeaderProps {
  handleDownload: (url: string, type: string) => void;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setMediaItem: React.Dispatch<React.SetStateAction<CreativeReportMediaItem | undefined>>;
  url: string;
  type: string;
  isExpanded?: boolean;
  hideExpandBtn?: boolean;
}

const MediaHeader: React.FC<MediaHeaderProps> = ({
  handleDownload,
  setExpanded,
  setMediaItem,
  url,
  type,
  isExpanded,
  hideExpandBtn,
}) => {
  const sanitizedURL = url ? (url.startsWith("http") ? url : `https://${url}`) : "";
  return (
    <div className="flex items-center justify-between px-2 py-1">
      <Button
        className="hover:bg-[#fff] px-2 py-1"
        onClick={() => void handleDownload(sanitizedURL, type)}
        variant="ghost"
      >
        <ArrowDownToLine size={16} />
      </Button>
      <div>
        {!hideExpandBtn && (
          <Button
            className="hover:bg-[#fff] px-2 py-1"
            variant="ghost"
            onClick={() => (isExpanded ? setExpanded(false) : setExpanded(true))}
          >
            {!isExpanded ? (
              <MoveDiagonal size={16} />
            ) : (
              <ChevronsDownUp size={16} className="rotate-45" />
            )}
          </Button>
        )}

        <Button
          className="hover:bg-[#fff] px-2 py-1"
          variant="ghost"
          onClick={() => setMediaItem(undefined)}
        >
          <X size={16} />
        </Button>
      </div>
    </div>
  );
};

interface MediaContentProps {
  source: string;
  type: string;
  thumbnail_url: string;
  isExpanded?: boolean;
}

const MediaContent: React.FC<MediaContentProps> = ({
  source,
  type,
  thumbnail_url,
  isExpanded,
}) => {
  const sanitizedSource = source
    ? source.startsWith("http")
      ? source
      : `https://${source}`
    : "about:blank";

  const [previewError, setPreviewError] = useState<boolean>(false);

  if (previewError) {
    return (
      <div className="relative w-80 h-60">
        <div className="absolute inset-0">
          <img
            className="w-auto h-full object-cover"
            loading="lazy"
            src={thumbnail_url}
            alt=""
          />
          <div className="absolute inset-0 bg-black bg-opacity-50" />
        </div>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-white text-lg mb-2">Unable to load {type}</p>
          <a
            href={sanitizedSource}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:underline"
          >
            Open {type} at source
          </a>
        </div>
      </div>
    );
  }

  if (type === "image") {
    return (
      <img
        src={sanitizedSource}
        alt=""
        className={
          isExpanded ? "max-h-[calc(80vh-44px)] w-auto object-contain" : "w-full"
        }
        loading="lazy"
        onError={() => setPreviewError(true)}
      />
    );
  }

  if (type === "video") {
    return (
      <ReactPlayer
        className="w-full h-full"
        url={sanitizedSource}
        playing
        onError={() => setPreviewError(true)}
        height="calc(80vh - 44px)"
        width="auto"
        style={{ maxWidth: "90vw" }}
        config={{
          facebook: {
            appId: "473271085197056",
          },
        }}
        controls={true}
      />
    );
  }
  return null;
};

const MediaPreview = ({
  mediaItem,
  setMediaItem,
}: {
  mediaItem: CreativeReportMediaItem;
  setMediaItem: React.Dispatch<React.SetStateAction<CreativeReportMediaItem | undefined>>;
}) => {
  const [expanded, setExpanded] = useState<boolean>(true);

  const onModalOpenChange = (open: boolean) => {
    setExpanded(open);
    if (!open) setMediaItem(undefined);
  };

  return (
    <Dialog open={expanded} onOpenChange={onModalOpenChange}>
      <DialogContent
        className="min-w-[300px] w-auto max-w-[90vw] max-h-[80vh] p-0 bg-white overflow-hidden gap-0 flex flex-col"
        hideCloseButton
      >
        <MediaHeader
          handleDownload={(url, type) =>
            void downloadMedia(url, type, undefined, mediaItem.account_name)
          }
          setExpanded={setExpanded}
          setMediaItem={setMediaItem}
          url={mediaItem.uncompressed_url || mediaItem.url}
          type={mediaItem.type}
          isExpanded={true}
          hideExpandBtn={true}
        />
        <div className="flex-1 flex justify-center items-center overflow-hidden">
          <MediaContent
            source={mediaItem.url}
            thumbnail_url={mediaItem?.thumbnail || mediaItem.url}
            type={mediaItem.type}
            isExpanded={true}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaPreview;
